/**
 * Hook del listado de pantallas DOOH (TG-59). Las pantallas se AUTO-REGISTRAN al
 * hacer polling del SMIL; aquí solo se leen y se refrescan.
 */
import { useCallback, useEffect, useState } from 'react';
import type { DoohScreen } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { doohApi } from '../api/doohApi';

export interface UseDoohScreensResult {
  data: DoohScreen[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudieron cargar las pantallas.';
}

export function useDoohScreens(): UseDoohScreensResult {
  const [data, setData] = useState<DoohScreen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    doohApi
      .listScreens()
      .then((response) => {
        if (!cancelled) setData(response.data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(toErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { data, loading, error, refresh };
}
